import { generatedApi } from './api.generated';
import {
  createEndpointExports,
  createHookExports,
  provideListTags,
  LIST_TAG_ID,
} from '@skynet/data-helpers';
import type { HookExports } from '@skynet/data-helpers';
import { DEX_VAULTS_API_ENDPOINTS as cfg } from './config';

/**
 * Cache tags follow the recommended `id: 'LIST'` recipe:
 * https://redux-toolkit.js.org/rtk-query/usage/automated-refetching#recipes
 *
 * Two complementary patterns are used here:
 *
 * 1. Top-level `Vaults` collection (LIST pattern).
 *    `listVaults` provides a per-item tag for every vault plus a dedicated
 *    `{ type: 'Vaults', id: 'LIST' }` tag, and `getVaultDetails` provides the
 *    per-vault tag, so that:
 *    - updating / deleting / status-changing a single vault invalidates only
 *      that vault's `{ id }` tag, refetching the affected detail query and the
 *      list (the list provides the same per-item tag);
 *    - creating a vault invalidates `{ id: 'LIST' }` to refetch the collection
 *      without touching unrelated entities.
 *    The list response is paginated/wrapped (`result.data.data` is the vault
 *    array), so per-item tags are derived from `result.data.data`.
 *
 * 2. Scoped sub-collections (`listVaultStats`, `listExecutors`).
 *    These are not part of one global collection — each is scoped to a parent
 *    vault. There is no meaningful global `LIST` for them, so the PARENT
 *    `vaultId` itself is used as the collection discriminator under dedicated
 *    `VaultStats` / `VaultExecutors` tag types. A mutation that changes a
 *    given vault's executors (`refreshVault`, `withdraw`, `convert`) invalidates
 *    `{ type: 'VaultExecutors', id: vaultId }` for that vault only, and fund-moving
 *    mutations (`withdraw`, `convert`) additionally invalidate
 *    `{ type: 'VaultStats', id: vaultId }`, leaving other vaults' sub-collections
 *    untouched.
 *
 * This replaces the previous broad `'DexVaults'` string tag plus the manual
 * `dispatch(...util.updateQueryData(...))` cache patching in `refreshVault`'s
 * `onQueryStarted` (attributed to redux-toolkit#5093). That issue concerns the
 * `isSuccess` flag during pessimistic updates, not tag invalidation —
 * `invalidatesTags` propagates correctly to the list and to scoped
 * sub-collections via the per-item / parent-id tags.
 */
const enhancedDexVaultsApi = generatedApi.enhanceEndpoints({
  addTagTypes: ['Vaults', 'VaultStats', 'VaultExecutors', 'ConversionToRaw'],
  endpoints: {
    // --- Top-level Vaults collection (LIST pattern) ---
    [cfg.listVaults.generated]: {
      providesTags: (result) => provideListTags(result?.data.data, 'Vaults'),
    },
    [cfg.getVaultDetails.generated]: {
      providesTags: (_result, _error, arg) => [{ type: 'Vaults', id: arg.vaultId }],
    },
    [cfg.getVaultBalance.generated]: {
      providesTags: (_result, _error, arg) => [{ type: 'Vaults', id: arg.vaultId }],
    },
    [cfg.getVaultSource.generated]: {
      providesTags: (_result, _error, arg) => [{ type: 'Vaults', id: arg.vaultId }],
    },
    [cfg.addVault.generated]: {
      invalidatesTags: [{ type: 'Vaults', id: LIST_TAG_ID }],
    },
    [cfg.updateVault.generated]: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Vaults', id: arg.updateVaultRequest.pid },
      ],
    },
    [cfg.deleteVault.generated]: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'Vaults', id: arg.vaultId }],
    },
    [cfg.updateVaultStatus.generated]: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'Vaults', id: arg.vaultId }],
    },
    [cfg.withdraw.generated]: {
      // Withdraw moves funds for a single vault (the vault id is the request `pid`):
      // it changes the vault balance/detail, the executors' balances, and produces
      // a transaction that affects the vault's stats — so invalidate all three
      // scoped collections for that vault, matching the pre-refactor behavior where
      // they all shared one `{ DexVaults, id: vaultId }` tag.
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Vaults', id: arg.withdrawRequest.pid },
        { type: 'VaultExecutors', id: arg.withdrawRequest.pid },
        { type: 'VaultStats', id: arg.withdrawRequest.pid },
      ],
    },
    [cfg.convert.generated]: {
      // Convert moves funds for a single vault (the vault id is the request `pid`),
      // affecting the same scoped collections as withdraw.
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Vaults', id: arg.convertRequest.pid },
        { type: 'VaultExecutors', id: arg.convertRequest.pid },
        { type: 'VaultStats', id: arg.convertRequest.pid },
      ],
    },
    [cfg.refreshVault.generated]: {
      // Refreshing a vault re-reads its on-chain balance/prices (vault detail) and
      // its executors' balances (scoped sub-collection), so invalidate both the
      // vault entity and that vault's scoped executors collection.
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Vaults', id: arg.vaultId },
        { type: 'VaultExecutors', id: arg.vaultId },
      ],
    },

    // --- Scoped sub-collection: per-vault stats ---
    // The stats response is a bare array of direction buckets with no item id,
    // so only the parent-scoped collection tag is provided.
    [cfg.listVaultStats.generated]: {
      providesTags: (_result, _error, arg) => [{ type: 'VaultStats', id: arg.vaultId }],
    },

    // --- Scoped sub-collection: per-vault executors ---
    // The executors response is wrapped (`result.data.executors`). Each executor
    // gets a per-item tag keyed by `${vaultId}-${executorId}`, plus the parent
    // `{ type: 'VaultExecutors', id: vaultId }` collection tag.
    [cfg.listExecutors.generated]: {
      providesTags: (result, _error, arg) =>
        result
          ? [
              ...result.data.executors.map((executor) => ({
                type: 'VaultExecutors' as const,
                id: `${arg.vaultId}-${executor.id}`,
              })),
              { type: 'VaultExecutors', id: arg.vaultId },
            ]
          : [{ type: 'VaultExecutors', id: arg.vaultId }],
    },

    // --- Conversion helper (independent cache, unchanged) ---
    [cfg.toRaw.generated]: {
      providesTags: (_result, _error, arg) => [
        { type: 'ConversionToRaw', id: `${arg.amount}-${arg.decimals}` },
      ],
    },
  },
});

export const dexVaultsApi: typeof enhancedDexVaultsApi = enhancedDexVaultsApi;

export const hookExports: HookExports<typeof cfg, typeof dexVaultsApi> = createHookExports(
  cfg,
  dexVaultsApi,
);

const endpointExports = createEndpointExports(cfg, dexVaultsApi);

export const dexVaultsApiEndpoints: typeof endpointExports = endpointExports;
